import { useChatStore } from '@/store/chatStore'
import { LanguageSelector } from '@/components/shared/LanguageSelector'
import { useChatHistory } from '@/hooks/useChatHistory'

// ─────────────────────────────────────────────
// ChatHeader
// Top bar of the chat panel.
// Shows: logo + title | language selector
//        history button | new chat | close
// ─────────────────────────────────────────────

interface Props {
  onClose?: () => void  // Only present in embed (floating) mode
}

export function ChatHeader({ onClose }: Props) {
  const config        = useChatStore((s) => s.config)
  const toggleSidebar = useChatStore((s) => s.toggleSidebar)
  const startNewChat  = useChatStore((s) => s.startNewChat)
  const isSidebarOpen = useChatStore((s) => s.isSidebarOpen)
  const { loadSessions } = useChatHistory()

  function handleHistoryClick() {
    loadSessions()
    toggleSidebar()
  }

  return (
    <div className="
      flex items-center justify-between
      px-4 py-3
      bg-[var(--color-brand)]
      rounded-t-widget
    ">
      {/* Left — logo + title */}
      <div className="flex items-center gap-2">
        {config?.logoUrl ? (
          <img
            src={config.logoUrl}
            alt="logo"
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <div className="
            w-7 h-7 rounded-full bg-white/20
            flex items-center justify-center
          ">
            <span className="text-white text-xs font-bold">D</span>
          </div>
        )}
        <span className="text-white font-semibold text-sm truncate max-w-[140px]">
          {config?.title || 'DirayahAI'}
        </span>
      </div>

      {/* Right — controls */}
      <div className="flex items-center gap-2">
        <LanguageSelector />

        {/* Chat history toggle */}
        <button
          onClick={handleHistoryClick}
          title="Chat history"
          className={`
            w-7 h-7 rounded-lg flex items-center justify-center
            transition-colors
            ${isSidebarOpen
              ? 'bg-white/30 text-white'
              : 'text-white/80 hover:bg-white/20 hover:text-white'}
          `}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
            fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd"
              d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z"
              clipRule="evenodd" />
          </svg>
        </button>

        {/* New chat */}
        <button
          onClick={startNewChat}
          title="New chat"
          className="
            w-7 h-7 rounded-lg flex items-center justify-center
            text-white/80 hover:bg-white/20 hover:text-white
            transition-colors
          "
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
            fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd"
              d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z"
              clipRule="evenodd" />
          </svg>
        </button>

        {/* Close — only in embed/floating mode */}
        {onClose && (
          <button
            onClick={onClose}
            title="Close"
            className="
              w-7 h-7 rounded-lg flex items-center justify-center
              text-white/80 hover:bg-white/20 hover:text-white
              transition-colors
            "
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd"
                d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}